import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { roomsAPI, bookingsAPI } from '../lib/api'
import type { Room } from '../types'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { useToast } from '../components/ui/useToast'
import {
  Plus, Edit, Trash2, MapPin, X, DollarSign, Users, TrendingUp,
  Activity, BarChart3, Calendar, Clock, CheckCircle, AlertCircle,
  Maximize2, Eye
} from 'lucide-react'

export function RoomsPageNew() {
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingRoom, setEditingRoom] = useState<Room | null>(null)
  const [deletingRoom, setDeletingRoom] = useState<Room | null>(null)
  const [viewingRoom, setViewingRoom] = useState<Room | null>(null)
  const { notify } = useToast()

  const loadRooms = async () => {
    setLoading(true)
    try {
      const res = await roomsAPI.list()
      if (res.success) {
        setRooms(Array.isArray(res.data) ? res.data as Room[] : [])
      }
    } catch (e) {
      notify({ kind: 'error', message: 'Failed to load rooms' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRooms()
  }, [])

  const handleDelete = async () => {
    if (!deletingRoom) return
    try {
      const res = await roomsAPI.delete(deletingRoom.id)
      if (res.success) {
        notify({ kind: 'success', message: 'Room deleted successfully' })
        setDeletingRoom(null)
        loadRooms()
      } else {
        notify({ kind: 'error', message: res.message || 'Failed to delete room' })
      }
    } catch (err) {
      notify({ kind: 'error', message: 'Failed to delete room' })
    }
  }

  // Calculate stats
  const stats = {
    total: rooms.length,
    active: rooms.filter((r: any) => r.total_bookings > 0).length,
    avgRate: rooms.length > 0
      ? Math.round(rooms.reduce((sum, r) => sum + (r.hourly_rate || 0), 0) / rooms.length)
      : 0,
    totalCapacity: rooms.reduce((sum, r) => sum + (r.capacity || 0), 0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Rooms & Spaces
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage studio rooms, capacity, and pricing
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add Room
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Rooms</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{stats.total}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <MapPin className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Active Rooms</p>
                <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">{stats.active}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Avg Hourly Rate</p>
                <p className="text-2xl font-bold mt-2 text-purple-900 dark:text-purple-100">£{stats.avgRate}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/5 dark:to-red-500/5 border-orange-200 dark:border-orange-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Total Capacity</p>
                <p className="text-3xl font-bold mt-2 text-orange-900 dark:text-orange-100">{stats.totalCapacity}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rooms Grid */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <MapPin className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">All Rooms ({rooms.length})</h2>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                </div>
              ))}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No rooms found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Get started by adding your first room or space
              </p>
              <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Room
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rooms.map((room: any) => (
                <div
                  key={room.id}
                  className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{room.name}</h3>
                      {room.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 line-clamp-2">{room.description}</p>
                      )}
                    </div>
                    <div className="p-2 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-500/20 dark:to-red-500/20">
                      <MapPin className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-3 w-3 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        {room.capacity || 0} people
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-3 w-3 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400">
                        £{room.hourly_rate || 0}/hr
                      </span>
                    </div>
                    {room.total_bookings !== undefined && (
                      <>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-600 dark:text-gray-400">
                            {room.total_bookings} bookings
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-600 dark:text-gray-400">
                            {room.total_bookings > 0 ? 'Active' : 'Idle'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewingRoom(room)}
                      className="flex-1 text-xs"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingRoom(room)}
                      className="text-xs"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingRoom(room)}
                      className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 text-xs"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <AddRoomModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            notify({ kind: 'success', message: 'Room added successfully' })
            setShowModal(false)
            loadRooms()
          }}
        />
      )}

      {editingRoom && (
        <EditRoomModal
          room={editingRoom}
          onClose={() => setEditingRoom(null)}
          onUpdated={() => {
            notify({ kind: 'success', message: 'Room updated successfully' })
            setEditingRoom(null)
            loadRooms()
          }}
        />
      )}

      {viewingRoom && (
        <RoomDetailModal
          room={viewingRoom}
          onClose={() => setViewingRoom(null)}
          onEdit={() => {
            setEditingRoom(viewingRoom)
            setViewingRoom(null)
          }}
        />
      )}

      {deletingRoom && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-bold">Delete Room</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingRoom.name}</span>? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setDeletingRoom(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AddRoomModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { register, handleSubmit, formState: { errors } } = useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true)
    try {
      await roomsAPI.create({
        name: data.name,
        description: data.description || undefined,
        capacity: data.capacity ? Number(data.capacity) : undefined,
        hourly_rate: data.hourly_rate ? Number(data.hourly_rate) * 100 : undefined
      })
      onCreated()
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Add Room</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Create a new room or space</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              Room name *
            </label>
            <Input {...register('name', { required: true })} placeholder="e.g., Studio A" />
            {errors.name && <p className="text-sm text-red-600 mt-1">Name is required</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Maximize2 className="h-4 w-4 text-gray-500" />
              Description
            </label>
            <Textarea {...register('description')} rows={3} placeholder="Optional description..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                Capacity
              </label>
              <Input {...register('capacity')} type="number" min="0" placeholder="e.g., 10" />
            </div>
            <div>
              <label className="form-label flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                Hourly Rate (£)
              </label>
              <Input {...register('hourly_rate')} type="number" step="0.01" min="0" placeholder="e.g., 25.00" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Creating...' : 'Create Room'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditRoomModal({ room, onClose, onUpdated }: { room: Room; onClose: () => void; onUpdated: () => void }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: room.name,
      description: room.description || '',
      capacity: room.capacity || 0,
      hourly_rate: room.hourly_rate ? room.hourly_rate / 100 : 0
    }
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true)
    try {
      await roomsAPI.update(room.id, {
        name: data.name,
        description: data.description || undefined,
        capacity: data.capacity ? Number(data.capacity) : undefined,
        hourly_rate: data.hourly_rate ? Number(data.hourly_rate) * 100 : undefined
      })
      onUpdated()
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Edit Room</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Update room information</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              Room name *
            </label>
            <Input {...register('name', { required: true })} />
            {errors.name && <p className="text-sm text-red-600 mt-1">Name is required</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Maximize2 className="h-4 w-4 text-gray-500" />
              Description
            </label>
            <Textarea {...register('description')} rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                Capacity
              </label>
              <Input {...register('capacity')} type="number" min="0" />
            </div>
            <div>
              <label className="form-label flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                Hourly Rate (£)
              </label>
              <Input {...register('hourly_rate')} type="number" step="0.01" min="0" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function RoomDetailModal({ room, onClose, onEdit }: { room: any; onClose: () => void; onEdit: () => void }) {
  const [bookings, setBookings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const res = await bookingsAPI.list({ room_id: room.id })
        if (res.success) {
          setBookings(Array.isArray(res.data) ? res.data : [])
        }
      } catch (e) {
        console.error('Failed to load bookings', e)
      } finally {
        setLoading(false)
      }
    }
    loadBookings()
  }, [room.id])

  const totalRevenue = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0)
  const utilization = bookings.length > 0 ? Math.round((bookings.filter(b => b.status === 'confirmed').length / bookings.length) * 100) : 0

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">{room.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{room.description || 'No description'}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
              <div className="card-body text-center">
                <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{bookings.length}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Total Bookings</p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/10 dark:to-emerald-500/10">
              <div className="card-body text-center">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">£{(totalRevenue / 100).toFixed(2)}</p>
                <p className="text-xs text-green-600 dark:text-green-400">Revenue Generated</p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10">
              <div className="card-body text-center">
                <Activity className="h-6 w-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{utilization}%</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">Utilization</p>
              </div>
            </div>
          </div>

          {/* Room Info */}
          <div className="card">
            <div className="card-header">
              <h4 className="font-semibold">Room Information</h4>
            </div>
            <div className="card-body grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Capacity</p>
                <p className="text-lg font-semibold">{room.capacity || 0} people</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Hourly Rate</p>
                <p className="text-lg font-semibold">£{((room.hourly_rate || 0) / 100).toFixed(2)}/hr</p>
              </div>
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="card">
            <div className="card-header flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-600" />
              <h4 className="font-semibold">Recent Bookings</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No bookings for this room yet
                </div>
              ) : (
                <div className="space-y-2">
                  {bookings.slice(0, 10).map(b => (
                    <div key={b.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {new Date(b.start_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - {new Date(b.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">£{((b.total_amount || 0) / 100).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300' :
                          b.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300' :
                          'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
